/* eslint-disable max-lines-per-function */
import { animate } from "framer-motion";
import { memo, useCallback, useEffect, useRef } from "react";

interface GlowingEffectProps {
  blur?: number;
  inactiveZone?: number;
  proximity?: number;
  spread?: number;
  variant?: "default" | "white";
  glow?: boolean;
  className?: string;
  disabled?: boolean;
  movementDuration?: number;
  borderWidth?: number;
}

const GlowingEffect = memo(
  ({
    blur = 0,
    inactiveZone = 0.7,
    proximity = 0,
    spread = 20,
    variant = "default",
    glow = false,
    className,
    movementDuration = 2,
    borderWidth = 1,
    disabled = true,
  }: GlowingEffectProps) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const lastPosition = useRef({ x: 0, y: 0 });
    const animationFrameRef = useRef<number>(0);
    const lastUpdateTime = useRef<number>(0);

    // Detect Safari for performance optimizations
    const isSafari = useRef(
      typeof navigator !== "undefined" && /^((?!chrome|android).)*safari/i.test(navigator.userAgent),
    );

    const handleMove = useCallback(
      (e?: MouseEvent | { x: number; y: number }) => {
        if (!containerRef.current) return;

        // Throttle updates more aggressively on Safari (60fps → 30fps)
        const now = Date.now();
        const throttleMs = isSafari.current ? 33 : 16; // 30fps vs 60fps
        if (now - lastUpdateTime.current < throttleMs) {
          return;
        }
        lastUpdateTime.current = now;

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }

        animationFrameRef.current = requestAnimationFrame(() => {
          const element = containerRef.current;
          if (!element) return;

          const { left, top, width, height } = element.getBoundingClientRect();
          const mouseX = e?.x ?? lastPosition.current.x;
          const mouseY = e?.y ?? lastPosition.current.y;

          if (e) {
            lastPosition.current = { x: mouseX, y: mouseY };
          }

          const center = [left + width * 0.5, top + height * 0.5];
          const distanceFromCenter = Math.hypot(mouseX - center[0], mouseY - center[1]);
          const inactiveRadius = 0.5 * Math.min(width, height) * inactiveZone;

          if (distanceFromCenter < inactiveRadius) {
            element.style.setProperty("--active", "0");
            return;
          }

          const isActive =
            mouseX > left - proximity &&
            mouseX < left + width + proximity &&
            mouseY > top - proximity &&
            mouseY < top + height + proximity;

          element.style.setProperty("--active", isActive ? "1" : "0");

          if (!isActive) return;

          const currentAngle = Number.parseFloat(element.style.getPropertyValue("--start")) || 0;
          const targetAngle = (180 * Math.atan2(mouseY - center[1], mouseX - center[0])) / Math.PI + 90;

          const angleDiff = ((targetAngle - currentAngle + 180) % 360) - 180;
          const newAngle = currentAngle + angleDiff;

          // Use faster duration on Safari to reduce animation overhead
          const duration = isSafari.current ? movementDuration * 0.6 : movementDuration;

          animate(currentAngle, newAngle, {
            duration,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (value) => {
              element.style.setProperty("--start", String(value));
            },
          }).then(
            () => {
              // Animation complete
            },
            () => {
              // Animation error (non-critical)
            },
          );
        });
      },
      [inactiveZone, proximity, movementDuration],
    );

    useEffect(() => {
      if (disabled) return;

      const handleScroll = () => handleMove();
      const handlePointerMove = (e: PointerEvent) => handleMove(e);

      window.addEventListener("scroll", handleScroll, { passive: true });
      document.body.addEventListener("pointermove", handlePointerMove, {
        passive: true,
      });

      return () => {
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        window.removeEventListener("scroll", handleScroll);
        document.body.removeEventListener("pointermove", handlePointerMove);
      };
    }, [handleMove, disabled]);

    const cn = (...classes: (string | boolean | undefined)[]) => classes.filter(Boolean).join(" ");

    // Compute gradient based on Safari detection and variant
    const getGradient = (): string => {
      if (variant === "white") {
        return `repeating-conic-gradient(
                    from 236.84deg at 50% 50%,
                    var(--black),
                    var(--black) calc(25% / var(--repeating-conic-gradient-times))
                )`;
      }

      // Safari: simplified gradient (no multiple radial overlays)
      if (isSafari.current) {
        return `repeating-conic-gradient(
                    from 236.84deg at 50% 50%,
                    #dd7bbb 0%,
                    #d79f1e calc(25% / var(--repeating-conic-gradient-times)),
                    #5a922c calc(50% / var(--repeating-conic-gradient-times)), 
                    #4c7894 calc(75% / var(--repeating-conic-gradient-times)),
                    #dd7bbb calc(100% / var(--repeating-conic-gradient-times))
                )`;
      }

      // Chrome/Firefox: full gradient with radial overlays
      return `radial-gradient(circle, #dd7bbb 10%, #dd7bbb00 20%),
                radial-gradient(circle at 40% 40%, #d79f1e 5%, #d79f1e00 15%),
                radial-gradient(circle at 60% 60%, #5a922c 10%, #5a922c00 20%), 
                radial-gradient(circle at 40% 60%, #4c7894 10%, #4c789400 20%),
                repeating-conic-gradient(
                    from 236.84deg at 50% 50%,
                    #dd7bbb 0%,
                    #d79f1e calc(25% / var(--repeating-conic-gradient-times)),
                    #5a922c calc(50% / var(--repeating-conic-gradient-times)), 
                    #4c7894 calc(75% / var(--repeating-conic-gradient-times)),
                    #dd7bbb calc(100% / var(--repeating-conic-gradient-times))
                )`;
    };

    return (
      <>
        <div
          className={cn(
            "pointer-events-none absolute -inset-px hidden rounded-[inherit] border opacity-0 transition-opacity",
            glow && "opacity-100",
            variant === "white" && "border-white",
            disabled && "!block",
          )}
        />
        <div
          ref={containerRef}
          style={
            {
              "--blur": `${blur}px`,
              "--spread": spread,
              "--start": "0",
              "--active": "0",
              "--glowingeffect-border-width": `${borderWidth}px`,
              "--repeating-conic-gradient-times": "5",
              "--gradient": getGradient(),
            } as React.CSSProperties
          }
          className={cn(
            "pointer-events-none absolute inset-0 rounded-[inherit] opacity-100 transition-opacity",
            glow && "opacity-100",
            blur > 0 && "blur-[var(--blur)] ",
            className,
            disabled && "!hidden",
          )}
        >
          <div
            className={cn(
              "glow",
              "rounded-[inherit]",
              'after:content-[""] after:rounded-[inherit] after:absolute after:inset-[calc(-1*var(--glowingeffect-border-width))]',
              "after:[border:var(--glowingeffect-border-width)_solid_transparent]",
              // Safari: Removed background-attachment:fixed (major performance killer)
              "after:[background:var(--gradient)]",
              "after:opacity-[var(--active)] after:transition-opacity after:duration-300",
              "after:[mask-clip:padding-box,border-box]",
              "after:[mask-composite:intersect]",
              "after:[mask-image:linear-gradient(#0000,#0000),conic-gradient(from_calc((var(--start)-var(--spread))*1deg),#00000000_0deg,#fff,#00000000_calc(var(--spread)*2deg))]",
              // Add will-change hint for Safari to use GPU acceleration
              "after:will-change-[opacity,transform]",
            )}
          />
        </div>
      </>
    );
  },
);

GlowingEffect.displayName = "GlowingEffect";

export { GlowingEffect };
